/**
 * Normalization + formatting helpers for the Support admin dashboard.
 *
 * The ms-agents backend response shapes are defensive-normalized here so the
 * page renders even when payload keys vary (or when the service is unreachable
 * and everything falls back to empty).
 */

import { getAssignee, getAssigneeId } from "./ticketUtils";

const SECONDS_PER_HOUR = 3600;
/**
 * Coerce an unknown API payload into an array of records.
 * Accepts an array, `{ tickets }`, `{ agents }`, `{ data }`, or `{ items }`.
 *
 * @param {*} payload
 * @param {string[]} [keys] - Preferred object keys to unwrap.
 * @returns {Array<Object>}
 */
export const toList = (payload, keys = ["tickets", "agents", "items", "data", "results"]) => {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== "object") return [];
  for (const key of keys) {
    if (Array.isArray(payload[key])) return payload[key];
  }
  return [];
};

/**
 * Normalize a raw ticket into a stable shape used across the page.
 *
 * Everything from `priority` down is v1.9 (integration guide §13.1). Older
 * payloads simply leave those fields empty rather than breaking the row —
 * `priority` defaults to `normal` and `breached` keeps its derived fallback so
 * a pre-v1.9 backend still lights up the red zone.
 *
 * @param {Object} raw
 * @returns {Object}
 */
export const normalizeTicket = (raw = {}) => {
  const roomId = raw.room_id || raw.roomId || raw.id || raw.ticket_id || null;
  const status = (raw.status || "open").toLowerCase();
  const firstRepliedAt = raw.first_responded_at || raw.first_response_at || raw.first_replied_at || null;
  const firstReplyDueAt = raw.first_response_due_at || raw.first_reply_due_at || null;
  const breached =
    raw.sla_breached === true ||
    raw.breached === true ||
    Boolean(raw.first_response_breached_at) ||
    Boolean(raw.next_response_breached_at) ||
    (!firstRepliedAt && firstReplyDueAt ? new Date(firstReplyDueAt) < new Date() : false);
  const unreadCount = raw.unread_count ?? raw.unreadCount;

  return {
    roomId,
    displayId: raw.display_id ?? raw.number ?? roomId,
    subject: raw.subject || raw.title || "(no subject)",
    // Display name only — never the raw client_id UUID. Consumers resolve
    // missing names from the identity cache via `clientId`.
    client: raw.client_name || raw.client?.name || null,
    clientId: raw.client_id || raw.client?.id || raw.client?.user_id || null,
    createdAt: raw.created_at || raw.createdAt || null,
    // Pre-v1.9 free-text bucket. It coexists with department/type rather than
    // being migrated into either, so both are kept.
    category: (raw.category || "OTHER").toUpperCase(),
    status,
    ticketStatus: raw.ticket_status ? String(raw.ticket_status).toUpperCase() : null,
    // One definition, shared with the ticket header — `""` rather than null so
    // it binds directly to the owner `<select>`.
    assigneeId: getAssigneeId(raw) ?? "",
    // Usually absent — v1.9 ships the id, not the name, so the row resolves it
    // through the identity cache. Kept for payloads that do inline it.
    assigneeName: getAssignee(raw) || null,
    firstRepliedAt,
    firstReplyDueAt,
    breached,

    // v1.9
    priority: String(raw.priority || "normal").toLowerCase(),
    department: raw.department || null,
    type: raw.type || null,
    waitingOn: raw.waiting_on === "agent" || raw.waiting_on === "customer" ? raw.waiting_on : null,
    tags: Array.isArray(raw.tags)
      ? raw.tags.map((t) => (typeof t === "string" ? t : t?.name)).filter(Boolean)
      : [],
    snoozedUntil: raw.snoozed_until || null,
    snoozeReason: raw.snooze_reason || null,
    mergedIntoRoomId: raw.merged_into_room_id || null,
    nextResponseDueAt: raw.next_response_due_at || null,
    lastCustomerMessageAt: raw.last_customer_message_at || null,
    // `null`, not `0`, when absent — a confident zero would hide real unread.
    unreadCount: unreadCount == null ? null : Number(unreadCount) || 0,
    lastMessagePreview: raw.last_message_preview || "",
    lastMessageAt: raw.last_message_at || null,
    reopenCount: Number(raw.reopen_count) || 0,
    resolvedAt: raw.resolved_at || null,
    resolvedBy: raw.resolved_by || null,
  };
};

/**
 * Normalize a raw agent workload record.
 *
 * @param {Object} raw
 * @returns {Object}
 */
export const normalizeAgent = (raw = {}) => {
  // v1.9 reports declared availability in `status` (available | away | offline);
  // older payloads used it for shift state (overloaded | off_shift).
  const status = (raw.status || raw.shift_status || "").toLowerCase();
  const openCount = raw.open_tickets ?? raw.open_count ?? raw.openCount ?? raw.open ?? 0;
  const capacity = raw.capacity == null ? null : Number(raw.capacity);
  return {
    userId: raw.agent_user_id || raw.user_id || raw.userId || raw.id || "",
    name: raw.name || raw.agent_name || raw.display_name || raw.email || "Agent",
    openCount,
    capacity,
    departments: Array.isArray(raw.departments) ? raw.departments : [],
    // Declared availability — an agent can be connected and still Away.
    availability: status === "available" || status === "away" ? status : "offline",
    presence: (raw.presence || (status === "available" ? "online" : "offline")).toLowerCase(),
    overloaded:
      raw.at_capacity === true ||
      raw.overloaded === true ||
      status === "overloaded" ||
      (capacity != null && capacity > 0 && openCount >= capacity),
    offShift: raw.off_shift === true || status === "off_shift" || status === "offline" || status === "away",
  };
};

/**
 * Format a duration in seconds as a compact label — `45m`, `2h`, `1.5h`, `3d`.
 * Used for both SLA targets and the metrics strip.
 *
 * @param {number|null|undefined} seconds
 * @returns {string}
 */
export const formatDurationShort = (seconds) => {
  if (seconds === null || seconds === undefined || Number.isNaN(Number(seconds))) {
    return "—";
  }
  const total = Number(seconds);
  if (total < SECONDS_PER_HOUR) return `${Math.round(total / 60)}m`;
  const hours = total / SECONDS_PER_HOUR;
  if (hours < 24) return `${Number.isInteger(hours) ? hours : hours.toFixed(1)}h`;
  const days = hours / 24;
  return `${Number.isInteger(days) ? days : days.toFixed(1)}d`;
};

/**
 * Compact "age" label from a timestamp, e.g. `45m`, `2h`, `3d`.
 *
 * @param {string|null} iso
 * @param {Date} [now]
 * @returns {string}
 */
export const formatAge = (iso, now = new Date()) => {
  if (!iso) return "—";
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return "—";
  const mins = Math.max(0, Math.floor((now - then) / 60000));
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
};

/** Status → StatusBadge tone + human label. Covers room and workflow states. */
const STATUS_META = {
  open: { tone: "info", label: "Open" },
  active: { tone: "info", label: "Open" },
  pending: { tone: "brand", label: "Pending" },
  in_progress: { tone: "brand", label: "In progress" },
  escalated: { tone: "danger", label: "Escalated" },
  resolved: { tone: "success", label: "Resolved" },
  closed: { tone: "neutral", label: "Closed" },
  archived: { tone: "neutral", label: "Archived" },
};

/**
 * Map a ticket status to a badge tone + label.
 *
 * @param {string} status
 * @returns {{ tone: string, label: string }}
 */
export const statusMeta = (status) =>
  STATUS_META[String(status || "").toLowerCase()] || {
    tone: "neutral",
    label: status || "Unknown",
  };

/**
 * Ticket categories offered in filters + create modal.
 *
 * `category` is a FREE STRING on the wire (max 100). The backend stores and
 * exact-match filters it but never validates it, so this list is the whole
 * vocabulary — a category missing from here still round-trips fine on a ticket
 * that already has it, and renders as a chip, but becomes unreachable from any
 * category filter because nothing offers it as an option.
 *
 * PLAN and COACH are newer than the original four and were live on tickets
 * before they were listed here.
 */
export const CATEGORIES = [
  "BILLING",
  "PLAN",
  "APP_BUG",
  "COACH",
  "ACCOUNT",
  "OTHER",
];

/*
 * There is deliberately no client-side category → department map here.
 *
 * There WAS one, briefly: `PLAN` and `COACH` predated the backend's ability to
 * place them, so the console filled a department in to keep those tickets
 * routable. v1.9.1 made `Plan` and `Coach` real departments and taught the
 * backend the mapping (BILLING/PAYMENT → Billing, ACCOUNT/LOGIN → Account,
 * APP_BUG/APP/BUG → App, PLAN/SUBSCRIPTION → Plan, COACH/COACHING → Coach,
 * anything else → Other), so the stopgap became actively wrong — it filed plan
 * tickets into Billing instead of the new Plan pool.
 *
 * Omitting `department` lets the server decide, which is the only copy of that
 * rule that cannot go stale. An explicit choice by the agent still wins.
 */

/**
 * Unwrap `GET /support/views/counts` into the two maps the sidebar renders.
 *
 * These counts use the same predicates as the views they label, so they always
 * agree with what opening a view shows — which is exactly why the queue must
 * not page tickets to count them itself.
 *
 * @param {*} payload
 * @returns {{ views: Object<string, number>, departments: Object<string, number> }}
 */
export const extractViewCounts = (payload) => ({
  views: payload?.views && typeof payload.views === "object" ? payload.views : {},
  departments:
    payload?.departments && typeof payload.departments === "object"
      ? payload.departments
      : {},
});

/**
 * Summarize a bulk-action response.
 *
 * Partial success is the normal case — one room failing never aborts the rest —
 * so a 200 must never be reported as "all done" without reading `results`.
 *
 * @param {*} payload - `POST /support/tickets/bulk` response (or its `.data`).
 * @param {number} [requested] - How many ids were sent, for the total.
 * @returns {{ ok: number, failed: number, total: number, firstError: string|null }}
 */
export const summarizeBulkResults = (payload, requested) => {
  const results = toList(payload?.data ?? payload, ["results"]);
  const ok = results.filter((r) => r?.ok === true).length;
  const failures = results.filter((r) => r?.ok !== true);
  return {
    ok,
    failed: failures.length,
    total: results.length || requested || 0,
    firstError: failures.find((r) => r?.error)?.error || null,
  };
};
